import type { FourPillars, PillarPosition } from '../types/pillars';
import type { HeavenlyStem } from '../types/stems';
import type { Ohaeng } from '../types/elements';
import type { TenGod } from '../types/ten-gods';
import type { DayMasterStrength, StrengthLevel } from '../types/strength';
import type {
  GyeokGukType,
  GyeokGukAnalysis,
  GyeokGukYongSin,
  GyeokGukThresholds,
  GukFormatType,
  FormatStatus,
  PagyeokReason,
} from '../types/gyeok-guk';
import type { EarthlyBranch } from '../types/branches';
import type { Relation } from '../types/relations';
import { getHiddenStems } from '../core/hidden-stems';
import { getTenGod } from './ten-gods';
import { getTwelveStage } from './twelve-stages';
import { getStemElement, getElementRelation } from '../core/five-elements';
import { findEarthlyCombines } from '../relations/earthly-combine';
import { COMBINES } from '../relations/heavenly-combine';
import { stemIndex } from '../core/sexagenary';

/** 격국 판별 임계값 (기본값) */
const DEFAULT_JONGGANG_MIN_SCORE = 80;
const DEFAULT_JONGGANG_MIN_SUPPORT_RATIO = 0.7;
const DEFAULT_JONGYAK_MAX_SCORE = 15;

/** 재다신약 판정 신약 레벨 */
const JAEDA_WEAK_LEVELS: StrengthLevel[] = ['극약', '태약', '신약'];

function resolveThresholds(thresholds?: Partial<GyeokGukThresholds>): GyeokGukThresholds {
  return {
    jonggangMinScore: thresholds?.jonggangMinScore ?? DEFAULT_JONGGANG_MIN_SCORE,
    jonggangMinSupportRatio: thresholds?.jonggangMinSupportRatio ?? DEFAULT_JONGGANG_MIN_SUPPORT_RATIO,
    jongyakMaxScore: thresholds?.jongyakMaxScore ?? DEFAULT_JONGYAK_MAX_SCORE,
    useAllHiddenStems: thresholds?.useAllHiddenStems ?? false,
    allowRestrainedGuk: thresholds?.allowRestrainedGuk ?? false,
    preferRegularFormat: thresholds?.preferRegularFormat ?? false,
  };
}

const TEN_GOD_TO_FORMAT: Partial<Record<TenGod, GyeokGukType>> = {
  '식신': '식신격',
  '상관': '상관격',
  '편재': '편재격',
  '정재': '정재격',
  '편관': '편관격',
  '정관': '정관격',
  '편인': '편인격',
  '정인': '정인격',
};

/** 십신 → 오행 범주 */
function tenGodCategory(tg: TenGod): '비겁' | '식상' | '재성' | '관성' | '인성' {
  switch (tg) {
    case '비견': case '겁재': return '비겁';
    case '식신': case '상관': return '식상';
    case '편재': case '정재': return '재성';
    case '편관': case '정관': return '관성';
    case '편인': case '정인': return '인성';
  }
}

/** 십신 범주 타입 */
type TenGodCategoryType = '비겁' | '식상' | '재성' | '관성' | '인성';

/** 일간 기준 십신 범주별 대표 오행을 구한다 */
function getCategoryElement(dayMaster: HeavenlyStem, category: TenGodCategoryType): Ohaeng {
  const dmElement = getStemElement(dayMaster);
  const rel = getElementRelation(dmElement);
  switch (category) {
    case '비겁': return dmElement;
    case '식상': return rel.generates;
    case '재성': return rel.overcomes;
    case '관성': return rel.overcomeBy;
    case '인성': return rel.generatedBy;
  }
}

/**
 * 격국(格局) 분석
 *
 * 판정 순서:
 * 1. 종격 체크 (극약/극왕)
 * 2. 국(局) 체크 (삼합/방합 기반 특수격)
 * 3. 건록격/양인격 체크 (월지 십이운성)
 * 4. 정격 판정 (월지 지장간 투출 분석)
 */
export function analyzeGyeokGuk(
  fourPillars: FourPillars,
  dayMasterStrength: DayMasterStrength,
  noHour?: boolean,
  thresholds?: Partial<GyeokGukThresholds>,
): GyeokGukAnalysis {
  const dayMaster = fourPillars.day.stem;
  const stemPositions = noHour ? (['year', 'month'] as const) : (['year', 'month', 'hour'] as const);
  const allPositions = noHour ? (['year', 'month', 'day'] as const) : (['year', 'month', 'day', 'hour'] as const);
  const t = resolveThresholds(thresholds);

  // 1. 종격 체크
  const jongResult = checkJongFormat(fourPillars, dayMasterStrength, stemPositions, allPositions, t);
  if (jongResult) {
    const yongSin = determineFormatYongSin(jongResult.type, dayMaster);
    return {
      type: jongResult.type,
      category: '종격',
      status: '성격',
      pagyeokReasons: [],
      formatYongSin: yongSin,
      description: jongResult.description,
    };
  }

  // 2. 국(局) 체크
  const gukResult = checkGukFormat(fourPillars, stemPositions, allPositions, t.allowRestrainedGuk);
  if (gukResult) {
    const yongSin = determineFormatYongSin(gukResult.type, dayMaster);
    return {
      type: gukResult.type,
      category: '국',
      definingRelation: gukResult.definingRelation,
      status: '성격',
      pagyeokReasons: [],
      formatYongSin: yongSin,
      description: gukResult.description,
    };
  }

  // 3. 건록격/양인격 체크 (preferRegularFormat이면 건너뛰고 정격 판정으로)
  const monthBranch = fourPillars.month.branch;
  const monthStage = getTwelveStage(dayMaster, monthBranch);

  if (!t.preferRegularFormat) {
    if (monthStage.stage === '건록') {
      return buildSpecialFormat('건록격', fourPillars, dayMaster, dayMasterStrength, stemPositions);
    }
    if (monthStage.stage === '제왕') {
      return buildSpecialFormat('양인격', fourPillars, dayMaster, dayMasterStrength, stemPositions);
    }
  }

  // 4. 정격 판정 (월지 지장간)
  return determineRegularFormat(fourPillars, dayMaster, dayMasterStrength, stemPositions);
}

/** 국(局) 격 판정 조건 */
const GUK_CONDITIONS: {
  type: GukFormatType;
  dayElement: Ohaeng;
  combineElement: Ohaeng;
  restraintElement: Ohaeng;
}[] = [
  { type: '곡직격', dayElement: '木', combineElement: '木', restraintElement: '金' },
  { type: '염상격', dayElement: '火', combineElement: '火', restraintElement: '水' },
  { type: '종혁격', dayElement: '金', combineElement: '金', restraintElement: '火' },
  { type: '윤하격', dayElement: '水', combineElement: '水', restraintElement: '土' },
];

/** 가색격에 필요한 토 지지 (辰戌丑未) */
const EARTH_BRANCHES: EarthlyBranch[] = ['辰', '戌', '丑', '未'];

/** 국(局) 체크: 삼합/방합 기반 특수격 */
function checkGukFormat(
  fourPillars: FourPillars,
  stemPositions: readonly ('year' | 'month' | 'hour')[],
  branchPositions: readonly ('year' | 'month' | 'day' | 'hour')[],
  allowRestrained = false,
): { type: GukFormatType; definingRelation: Relation; description: string } | null {
  const dayMaster = fourPillars.day.stem;
  const dayElement = getStemElement(dayMaster);

  // 천간의 오행 집합
  const stemElements = new Set<Ohaeng>();
  for (const pos of stemPositions) {
    stemElements.add(getStemElement(fourPillars[pos].stem));
  }

  // 지지 정보
  const branchInputs = branchPositions.map(pos => ({
    branch: fourPillars[pos].branch,
    position: pos,
  }));

  // 가색격 별도 판정 (辰戌丑未 3개 이상)
  if (dayElement === '土') {
    const earthBranchInputs = branchInputs.filter(b => EARTH_BRANCHES.includes(b.branch));
    if (earthBranchInputs.length >= 3 && (allowRestrained || !stemElements.has('木'))) {
      const earthBranches = earthBranchInputs.map(b => b.branch);
      const relation: Relation = {
        type: '사고합',
        pillars: earthBranchInputs.map(b => b.position),
        branches: earthBranches,
        result: '土',
        description: `${earthBranches.join('')} 사고합 → 土`,
      };
      return {
        type: '가색격',
        definingRelation: relation,
        description: `일간 ${dayMaster}(土)이고 ${relation.description}으로 가색격`,
      };
    }
  }

  // 삼합/방합 기반 4종 (곡직격, 염상격, 종혁격, 윤하격)
  const combines = findEarthlyCombines(branchInputs);
  const relevantCombines = combines.filter(
    r => (r.type === '삼합' || r.type === '방합') && r.result != null
  );

  for (const cond of GUK_CONDITIONS) {
    if (dayElement !== cond.dayElement) continue;

    // 일간 오행과 일치하는 삼합/방합 찾기
    const matchingCombine = relevantCombines.find(r => r.result === cond.combineElement);
    if (!matchingCombine) continue;

    // 천간(년/월/시)에 극제 오행이 없어야 함 (allowRestrained이면 무시)
    if (!allowRestrained && stemElements.has(cond.restraintElement)) continue;

    return {
      type: cond.type,
      definingRelation: matchingCombine,
      description: `일간 ${dayMaster}(${cond.dayElement})이고 ${matchingCombine.description}으로 ${cond.type}`,
    };
  }

  return null;
}

/** 종격 체크 */
function checkJongFormat(
  fourPillars: FourPillars,
  strength: DayMasterStrength,
  stemPositions: readonly ('year' | 'month' | 'hour')[],
  allPositions: readonly ('year' | 'month' | 'day' | 'hour')[],
  t: GyeokGukThresholds,
): { type: GyeokGukType; description: string } | null {
  const dayMaster = fourPillars.day.stem;
  const { score } = strength;

  // 종강격: 극왕/태강 + 비겁·인성 비율 이상
  if (score >= t.jonggangMinScore) {
    const categoryRatio = getCategoryRatio(fourPillars, dayMaster, stemPositions, allPositions, t.useAllHiddenStems);
    const supportRatio = categoryRatio['비겁'] + categoryRatio['인성'];
    if (supportRatio >= t.jonggangMinSupportRatio) {
      return {
        type: '종강격',
        description: `일간이 극왕(${score.toFixed(1)})하고 비겁·인성이 ${(supportRatio * 100).toFixed(0)}%로 종강격`,
      };
    }
  }

  // 종약(종아/종재/종살): 극약/태약 + 천간에 비겁/인성 없음
  if (score <= t.jongyakMaxScore) {
    // 천간에 비겁/인성 있으면 종격 불가 → 정격으로
    if (hasSupportingStem(fourPillars, dayMaster, stemPositions)) {
      return null;
    }

    const categoryRatio = getCategoryRatio(fourPillars, dayMaster, stemPositions, allPositions, t.useAllHiddenStems);
    // dominant 범주 결정
    const dominant = getDominantCategory(categoryRatio);
    if (dominant === '식상') {
      return {
        type: '종아격',
        description: `일간이 극약(${score.toFixed(1)})하고 식상이 주도하여 종아격`,
      };
    }
    if (dominant === '재성') {
      return {
        type: '종재격',
        description: `일간이 극약(${score.toFixed(1)})하고 재성이 주도하여 종재격`,
      };
    }
    if (dominant === '관성') {
      return {
        type: '종살격',
        description: `일간이 극약(${score.toFixed(1)})하고 관성이 주도하여 종살격`,
      };
    }
  }

  return null;
}

/** 사주 전체 십신 범주별 비율 계산 (일간 제외) */
function getCategoryRatio(
  fourPillars: FourPillars,
  dayMaster: HeavenlyStem,
  stemPositions: readonly ('year' | 'month' | 'hour')[],
  branchPositions: readonly ('year' | 'month' | 'day' | 'hour')[],
  useAllHiddenStems = false,
): Record<string, number> {
  const counts: Record<string, number> = {
    '비겁': 0, '식상': 0, '재성': 0, '관성': 0, '인성': 0,
  };
  let total = 0;

  for (const pos of stemPositions) {
    const tg = getTenGod(dayMaster, fourPillars[pos].stem);
    counts[tenGodCategory(tg)]++;
    total++;
  }

  // 지지 장간: 본기만 or 전체(본기+중기+여기)
  for (const pos of branchPositions) {
    const hidden = getHiddenStems(fourPillars[pos].branch);
    if (useAllHiddenStems) {
      for (const stem of hidden) {
        const tg = getTenGod(dayMaster, stem);
        counts[tenGodCategory(tg)]++;
        total++;
      }
    } else {
      const mainTg = getTenGod(dayMaster, hidden[hidden.length - 1]);
      counts[tenGodCategory(mainTg)]++;
      total++;
    }
  }

  const ratio: Record<string, number> = {};
  for (const key of Object.keys(counts)) {
    ratio[key] = total > 0 ? counts[key] / total : 0;
  }
  return ratio;
}

/** 천간(년/월/시)에 비겁 또는 인성이 있는지 체크 */
function hasSupportingStem(
  fourPillars: FourPillars,
  dayMaster: HeavenlyStem,
  stemPositions: readonly ('year' | 'month' | 'hour')[],
): boolean {
  for (const pos of stemPositions) {
    const tg = getTenGod(dayMaster, fourPillars[pos].stem);
    const cat = tenGodCategory(tg);
    if (cat === '비겁' || cat === '인성') return true;
  }
  return false;
}

/** 비겁/인성 제외하고 가장 높은 범주 반환 */
function getDominantCategory(ratio: Record<string, number>): string {
  const candidates = ['식상', '재성', '관성'] as const;
  return candidates.reduce((maxCat, currentCat) =>
    ratio[currentCat] > ratio[maxCat] ? currentCat : maxCat
  );
}

/** 건록격/양인격 빌드 */
function buildSpecialFormat(
  type: '건록격' | '양인격',
  fourPillars: FourPillars,
  dayMaster: HeavenlyStem,
  dayMasterStrength: DayMasterStrength,
  stemPositions: readonly ('year' | 'month' | 'hour')[],
): GyeokGukAnalysis {
  const yongSin = determineFormatYongSin(type, dayMaster);
  const pagyeokReasons = assessPagyeok(type, fourPillars, dayMaster, dayMasterStrength, stemPositions);
  const status: FormatStatus = pagyeokReasons.length > 0 ? '파격' : '성격';
  const stageLabel = type === '건록격' ? '건록' : '제왕';

  return {
    type,
    category: type,
    definingStem: dayMaster,
    definingTenGod: '비견',
    status,
    pagyeokReasons,
    formatYongSin: yongSin,
    description: `월지가 일간 ${dayMaster}의 ${stageLabel} 위치로 ${type}`,
  };
}

/** 정격 판정: 월지 지장간 분석 + 투출 */
function determineRegularFormat(
  fourPillars: FourPillars,
  dayMaster: HeavenlyStem,
  dayMasterStrength: DayMasterStrength,
  stemPositions: readonly ('year' | 'month' | 'hour')[],
): GyeokGukAnalysis {
  const monthBranch = fourPillars.month.branch;
  const hiddenStems = getHiddenStems(monthBranch);

  // 비견/겁재가 아닌 지장간 필터
  const candidates: { stem: HeavenlyStem; tenGod: TenGod; index: number }[] = [];
  for (let i = 0; i < hiddenStems.length; i++) {
    const tg = getTenGod(dayMaster, hiddenStems[i]);
    const cat = tenGodCategory(tg);
    if (cat !== '비겁') {
      candidates.push({ stem: hiddenStems[i], tenGod: tg, index: i });
    }
  }

  // 투출된 것 우선
  const transparent = candidates.filter(c => isTuchul(c.stem, fourPillars, stemPositions));
  let chosen: { stem: HeavenlyStem; tenGod: TenGod; index: number };

  if (transparent.length > 0) {
    // 투출된 것 중 정기(본기) 순서 (index 큰 것 우선)
    chosen = transparent.reduce((a, b) => a.index > b.index ? a : b);
  } else if (candidates.length > 0) {
    // 투출 없으면 정기(본기) 순서
    chosen = candidates.reduce((a, b) => a.index > b.index ? a : b);
  } else {
    // 모든 지장간이 비겁인 경우 (寅-甲, 卯-乙 등에서 일간이 같은 오행)
    // 이 경우 건록격/양인격으로 이미 처리되었어야 하지만 fallback
    const mainStem = hiddenStems[hiddenStems.length - 1];
    const mainTg = getTenGod(dayMaster, mainStem);
    chosen = { stem: mainStem, tenGod: mainTg, index: hiddenStems.length - 1 };
  }

  const formatType = TEN_GOD_TO_FORMAT[chosen.tenGod];
  if (!formatType) {
    // 비견/겁재만 남은 경우 → 건록격으로 fallback
    return buildSpecialFormat('건록격', fourPillars, dayMaster, dayMasterStrength, stemPositions);
  }

  const pagyeokReasons = assessPagyeok(formatType, fourPillars, dayMaster, dayMasterStrength, stemPositions);
  const status: FormatStatus = pagyeokReasons.length > 0 ? '파격' : '성격';
  const yongSin = determineFormatYongSin(formatType, dayMaster);

  return {
    type: formatType,
    category: '정격',
    definingStem: chosen.stem,
    definingTenGod: chosen.tenGod,
    status,
    pagyeokReasons,
    formatYongSin: yongSin,
    description: `월지 ${monthBranch}의 지장간 ${chosen.stem}(${chosen.tenGod})이 격국을 결정하여 ${formatType}`,
  };
}

/** 투출 판정: 같은 오행의 천간이 년/월/시주에 있으면 투출 */
function isTuchul(
  stem: HeavenlyStem,
  fourPillars: FourPillars,
  stemPositions: readonly ('year' | 'month' | 'hour')[],
): boolean {
  const element = getStemElement(stem);
  for (const pos of stemPositions) {
    if (getStemElement(fourPillars[pos].stem) === element) {
      return true;
    }
  }
  return false;
}

/** 파격 판정 컨텍스트 */
interface PagyeokContext {
  formatType: GyeokGukType;
  stemTenGods: Set<TenGod>;
  dayMasterStrength: DayMasterStrength;
  fourPillars: FourPillars;
  dayMaster: HeavenlyStem;
  stemPositions: readonly ('year' | 'month' | 'hour')[];
}

/** 파격 룰 정의 */
interface PagyeokRule {
  reason: PagyeokReason;
  appliesTo: GyeokGukType[];
  condition: (ctx: PagyeokContext) => boolean;
}

/**
 * 파격 룰 테이블
 *
 * 규칙 추가 시:
 * 1. src/types/gyeok-guk.ts의 PagyeokReason에 리터럴 추가
 * 2. 이 배열에 { reason, appliesTo, condition } 항목 추가
 */
const PAGYEOK_RULES: PagyeokRule[] = [
  {
    reason: '상관견관',
    appliesTo: ['정관격'],
    condition: ctx => {
      if (!ctx.stemTenGods.has('상관')) return false;

      // 해소 1: 인성 통관 — 정인/편인이 천간에 있으면 상관을 제어
      if (ctx.stemTenGods.has('정인') || ctx.stemTenGods.has('편인')) return false;

      // 해소 2: 모든 상관이 합거되면 정관을 극할 수 없음
      const allSanggwanCombined = ctx.stemPositions.every(pos => {
        if (getTenGod(ctx.dayMaster, ctx.fourPillars[pos].stem) !== '상관') return true;
        return isStemCombinedAt(ctx, pos);
      });
      if (allSanggwanCombined) return false;

      return true;
    },
  },
  {
    reason: '관살혼잡',
    appliesTo: ['정관격', '편관격'],
    condition: ctx => {
      const isMixed =
        (ctx.formatType === '정관격' && ctx.stemTenGods.has('편관')) ||
        (ctx.formatType === '편관격' && ctx.stemTenGods.has('정관'));
      if (!isMixed) return false;

      // 해소: 거일류일(去一留一) — 천간의 정관 또는 편관 중 한쪽이 전부 합거되면 혼잡 해소
      // 참고: 극(식신제살 등)에 의한 해소는 합거와 별개이며, 정관격에서의 식신제살은
      //   별도 이슈로 검토 필요 (무제화살 룰은 편관격 전용). 여기서는 합거만 판정.
      const allCombined = (target: TenGod) =>
        ctx.stemTenGods.has(target) && ctx.stemPositions.every(pos => {
          if (getTenGod(ctx.dayMaster, ctx.fourPillars[pos].stem) !== target) return true;
          return isStemCombinedAt(ctx, pos);
        });
      if (allCombined('편관') || allCombined('정관')) return false;

      return true;
    },
  },
  {
    reason: '무제화살',
    appliesTo: ['편관격'],
    condition: ctx => !ctx.stemTenGods.has('식신') && !ctx.stemTenGods.has('정인'),
  },
  {
    reason: '효신탈식',
    appliesTo: ['식신격'],
    condition: ctx => {
      if (!ctx.stemTenGods.has('편인')) return false;

      // 해소: 재성(편재/정재)이 천간에 있으면 편인을 극(재극인)하여 식신 보호
      if (ctx.stemTenGods.has('편재') || ctx.stemTenGods.has('정재')) return false;

      // 해소: 모든 편인이 합거되어야 해소 (하나라도 남으면 탈식 가능)
      const allPyeoninCombined = ctx.stemPositions.every(pos => {
        if (getTenGod(ctx.dayMaster, ctx.fourPillars[pos].stem) !== '편인') return true;
        return isStemCombinedAt(ctx, pos);
      });
      if (allPyeoninCombined) return false;

      return true;
    },
  },
  {
    reason: '재다신약',
    appliesTo: ['정재격', '편재격'],
    condition: ctx => {
      if (!JAEDA_WEAK_LEVELS.includes(ctx.dayMasterStrength.level)) return false;
      return countStemCategory(ctx.fourPillars, ctx.dayMaster, '재성', ctx.stemPositions) >= 2;
    },
  },
];

/** noHour 고려한 인접 기둥 목록을 반환 */
function getAdjacentPillars(
  position: PillarPosition,
  hasHour: boolean,
): PillarPosition[] {
  switch (position) {
    case 'year': return ['month'];
    case 'month': return ['year', 'day'];
    case 'day': return hasHour ? ['month', 'hour'] : ['month'];
    case 'hour': return ['day'];
  }
}

/** 특정 기둥 위치의 천간 stemIndex를 안전하게 가져온다 */
function getStemAt(ctx: PagyeokContext, pos: PillarPosition): number {
  if (pos === 'day') return stemIndex(ctx.dayMaster);
  return stemIndex(ctx.fourPillars[pos].stem);
}

/** 특정 위치의 천간이 인접 천간과 천간합(COMBINES)하는지 판정 (쟁합·noHour 고려) */
function isStemCombinedAt(ctx: PagyeokContext, position: PillarPosition): boolean {
  const sIdx = getStemAt(ctx, position);
  const hasHour = ctx.stemPositions.includes('hour');
  const adjacent = getAdjacentPillars(position, hasHour);

  // 각 천간은 합 상대가 1명뿐이므로 find로 partner 확인
  const combine = COMBINES.find(([c1, c2]) => sIdx === c1 || sIdx === c2);
  if (!combine) return false;
  const partner = sIdx === combine[0] ? combine[1] : combine[0];

  // subject 측 쟁합: 인접에 partner가 2명 이상이면 쟁합
  const partnerAdjs = adjacent.filter(adj => getStemAt(ctx, adj) === partner);
  if (partnerAdjs.length === 0) return false;
  if (partnerAdjs.length >= 2) return false; // subject 양쪽에 partner → 쟁합

  // partner 측 쟁합: partner의 다른 인접에도 같은 합 상대가 있으면 쟁합
  const adj = partnerAdjs[0];
  const partnerNeighbors = getAdjacentPillars(adj, hasHour);
  const contested = partnerNeighbors.some(pa => {
    if (pa === position) return false;
    return getStemAt(ctx, pa) === sIdx;
  });
  return !contested;
}

/** 파격 판정: 룰 테이블 순회 */
function assessPagyeok(
  formatType: GyeokGukType,
  fourPillars: FourPillars,
  dayMaster: HeavenlyStem,
  dayMasterStrength: DayMasterStrength,
  stemPositions: readonly ('year' | 'month' | 'hour')[],
): PagyeokReason[] {
  const ctx: PagyeokContext = {
    formatType,
    stemTenGods: getStemTenGods(fourPillars, dayMaster, stemPositions),
    dayMasterStrength,
    fourPillars,
    dayMaster,
    stemPositions,
  };

  return PAGYEOK_RULES
    .filter(rule => rule.appliesTo.includes(formatType) && rule.condition(ctx))
    .map(rule => rule.reason);
}

/** 년/월/시 천간의 십신 집합 */
function getStemTenGods(
  fourPillars: FourPillars,
  dayMaster: HeavenlyStem,
  stemPositions: readonly ('year' | 'month' | 'hour')[],
): Set<TenGod> {
  const result = new Set<TenGod>();
  for (const pos of stemPositions) {
    result.add(getTenGod(dayMaster, fourPillars[pos].stem));
  }
  return result;
}

/** 년/월/시 천간에서 특정 범주의 십신 개수 */
function countStemCategory(
  fourPillars: FourPillars,
  dayMaster: HeavenlyStem,
  category: TenGodCategoryType,
  stemPositions: readonly ('year' | 'month' | 'hour')[],
): number {
  let count = 0;
  for (const pos of stemPositions) {
    const tg = getTenGod(dayMaster, fourPillars[pos].stem);
    if (tenGodCategory(tg) === category) count++;
  }
  return count;
}

/** 격국 기반 용신 결정 */
function determineFormatYongSin(
  formatType: GyeokGukType,
  dayMaster: HeavenlyStem,
): GyeokGukYongSin {
  const table = FORMAT_YONGSHEN_TABLE[formatType];
  if (!table) {
    // fallback
    const dmElement = getStemElement(dayMaster);
    return {
      sunYong: getElementRelation(dmElement).generatedBy,
      description: `${formatType}의 용신`,
    };
  }

  const sunYong = getCategoryElement(dayMaster, table.sunYong);
  const yeokYong = table.yeokYong ? getCategoryElement(dayMaster, table.yeokYong) : undefined;

  let description: string;
  if (yeokYong) {
    description = `${formatType}의 순용은 ${table.sunYong}(${sunYong}), 역용은 ${table.yeokYong}(${yeokYong})`;
  } else {
    description = `${formatType}의 순용은 ${table.sunYong}(${sunYong})`;
  }

  return { sunYong, yeokYong, description };
}

const FORMAT_YONGSHEN_TABLE: Partial<Record<GyeokGukType, { sunYong: TenGodCategoryType; yeokYong?: TenGodCategoryType }>> = {
  '식신격': { sunYong: '재성' },
  '상관격': { sunYong: '인성', yeokYong: '재성' },
  '편재격': { sunYong: '관성', yeokYong: '식상' },
  '정재격': { sunYong: '관성', yeokYong: '식상' },
  '정관격': { sunYong: '인성', yeokYong: '재성' },
  '편관격': { sunYong: '식상', yeokYong: '인성' },
  '정인격': { sunYong: '관성' },
  '편인격': { sunYong: '재성', yeokYong: '식상' },
  '건록격': { sunYong: '관성', yeokYong: '재성' },
  '양인격': { sunYong: '관성', yeokYong: '재성' },
  '종강격': { sunYong: '비겁', yeokYong: '인성' },
  '종아격': { sunYong: '식상', yeokYong: '재성' },
  '종재격': { sunYong: '재성', yeokYong: '식상' },
  '종살격': { sunYong: '관성', yeokYong: '재성' },
  // 국(局) 격: 순용 원리 (왕성한 기세를 따름)
  '곡직격': { sunYong: '비겁', yeokYong: '인성' },
  '염상격': { sunYong: '비겁', yeokYong: '인성' },
  '가색격': { sunYong: '비겁', yeokYong: '인성' },
  '종혁격': { sunYong: '비겁', yeokYong: '인성' },
  '윤하격': { sunYong: '비겁', yeokYong: '인성' },
};
